import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ArrowRight,
    BarChart3,
    Bot,
    CheckCircle2,
    CircleDollarSign,
    LayoutDashboard,
    MousePointer2,
    TrendingUp,
} from "lucide-react";
import { languagePrefixes } from "../../i18n";

const VideoHero = ({
    videoSrc = "https://crino-cdn.nyc3.cdn.digitaloceanspaces.com/pages-images/homepage/crino-ai-funding.webm",
    poster = "",
}) => {
    const videoRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(true);
    const [activeFeature, setActiveFeature] = useState('ai');
    const [activeMetric, setActiveMetric] = useState('funding');
    const [activeRegion, setActiveRegion] = useState('global');
    const { t, i18n } = useTranslation();
    const currentLanguage = (i18n.language || 'en').split('-')[0];
    const languagePrefix = languagePrefixes[currentLanguage] || '';

    const createPath = (path) => `${languagePrefix}${path}`;
    const createMarketingPath = (path, params = {}) => {
        const search = new URLSearchParams(params).toString();
        return `${createPath(path)}${search ? `?${search}` : ''}`;
    };

    const features = [
        {
            id: 'ai',
            icon: Bot,
            badge: t('pageHome.content.newFeatures.aiBadge'),
            title: t('pageHome.content.newFeatures.aiTitle'),
            text: t('pageHome.content.newFeatures.aiText'),
            meta: t('pageHome.content.newFeatures.aiMeta'),
            href: createMarketingPath('/pricing', {
                utm_source: 'crino-homepage',
                utm_medium: 'new-features-ai-card',
                utm_campaign: 'professional-features',
                feature: 'ai-funding',
                intendedRoute: '/app/ai-funding',
            }),
        },
        {
            id: 'statistics',
            icon: BarChart3,
            badge: t('pageHome.content.newFeatures.statisticsBadge'),
            title: t('pageHome.content.newFeatures.statisticsTitle'),
            text: t('pageHome.content.newFeatures.statisticsText'),
            meta: t('pageHome.content.newFeatures.statisticsMeta'),
            href: createMarketingPath('/pricing', {
                utm_source: 'crino-homepage',
                utm_medium: 'new-features-statistics-card',
                utm_campaign: 'professional-features',
                feature: 'statistics',
                intendedRoute: '/app/statistics/overview',
            }),
        },
    ];

    const aiPreviewItems = [
        t('pageHome.content.newFeatures.aiPreviewContext'),
        t('pageHome.content.newFeatures.aiPreviewMatches'),
        t('pageHome.content.newFeatures.aiPreviewSignals'),
    ];

    const statisticsPreviewItems = [
        t('pageHome.content.newFeatures.statisticsPreviewTrends'),
        t('pageHome.content.newFeatures.statisticsPreviewBreakdowns'),
        t('pageHome.content.newFeatures.statisticsPreviewRankings'),
    ];

    const metricOptions = [
        {
            id: 'funding',
            label: t('pageHome.content.newFeatures.metricFunding'),
            value: '$4.8B',
            change: '+18%',
            bars: [44, 58, 76, 68, 92],
        },
        {
            id: 'deals',
            label: t('pageHome.content.newFeatures.metricDeals'),
            value: '1,240',
            change: '+11%',
            bars: [56, 48, 63, 72, 84],
        },
        {
            id: 'activity',
            label: t('pageHome.content.newFeatures.metricActivity'),
            value: '386',
            change: '+24%',
            bars: [38, 62, 55, 79, 88],
        },
    ];

    const regionOptions = [
        { id: 'global', label: t('pageHome.content.newFeatures.regionGlobal'), modifier: 0 },
        { id: 'northAmerica', label: t('pageHome.content.newFeatures.regionNorthAmerica'), modifier: -8 },
        { id: 'europe', label: t('pageHome.content.newFeatures.regionEurope'), modifier: -14 },
    ];

    const chartLabels = [
        t('pageHome.content.newFeatures.chartQ1'),
        t('pageHome.content.newFeatures.chartQ2'),
        t('pageHome.content.newFeatures.chartQ3'),
        t('pageHome.content.newFeatures.chartQ4'),
        t('pageHome.content.newFeatures.chartNow'),
    ];
    const selectedMetric = metricOptions.find((metric) => metric.id === activeMetric) || metricOptions[0];
    const selectedRegion = regionOptions.find((region) => region.id === activeRegion) || regionOptions[0];
    const chartBars = selectedMetric.bars.map((value, index) => Math.max(18, Math.min(100, value + selectedRegion.modifier + (index * 2))));
    const activeFeatureData = features.find((feature) => feature.id === activeFeature) || features[0];

    useEffect(() => {
        const q = window.matchMedia("(prefers-reduced-motion: reduce)");
        if (q.matches && videoRef.current) {
            videoRef.current.pause();
            setIsPlaying(false);
        }
    }, []);

    const togglePlay = () => {
        const v = videoRef.current;
        if (!v) return;
        if (isPlaying) v.pause();
        else v.play().catch(() => { });
        setIsPlaying((s) => !s);
    };

    return (
        <section className="relative w-full overflow-x-hidden bg-white">
            <div className="mx-auto max-w-screen-xl px-6 py-16 sm:px-12 lg:px-16 lg:py-24">
                <div className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm md:p-6 lg:p-8">
                    <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                        <div className="max-w-3xl">
                            <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700">
                                <CheckCircle2 className="h-3.5 w-3.5" />
                                {t('pageHome.content.newFeatures.badge')}
                            </div>
                            <h2 className="text-3xl font-bold leading-tight tracking-tight text-stone-950 sm:text-4xl">
                                {t('pageHome.content.newFeatures.title')}
                            </h2>
                            <p className="mt-3 max-w-2xl text-base leading-7 text-stone-600">
                                {t('pageHome.content.newFeatures.subtitle')}
                            </p>
                        </div>
                        <a
                            href={createMarketingPath('/signup', {
                                utm_source: 'crino-homepage',
                                utm_medium: 'new-features-primary-cta',
                                utm_campaign: 'dashboard-features',
                                intendedRoute: '/app/dashboard',
                            })}
                            className="inline-flex h-9 w-fit max-w-full shrink-0 items-center justify-center gap-2 rounded-full bg-stone-900 px-4 text-sm font-medium text-white shadow transition-colors hover:bg-stone-900/90"
                        >
                            <span>{t('pageHome.content.newFeatures.primaryCta')}</span>
                            <ArrowRight className="h-4 w-4" />
                        </a>
                    </div>

                    <div className="mt-6 grid grid-cols-1 gap-5 lg:grid-cols-12 lg:gap-6">
                        <div className="grid self-start content-start gap-3 sm:grid-cols-2 lg:col-span-5 lg:grid-cols-1">
                            {features.map(({ id, icon: Icon, badge, title, text, meta }) => {
                                const isActive = activeFeature === id;

                                return (
                                    <button
                                        type="button"
                                        key={title}
                                        onClick={() => setActiveFeature(id)}
                                        className={`group flex min-h-[176px] cursor-pointer flex-col justify-between rounded-lg border p-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-stone-400/70 ${isActive
                                            ? 'border-stone-900 bg-white shadow-sm'
                                            : 'border-stone-200 bg-stone-50/70 hover:border-stone-300 hover:bg-white'}`}
                                        aria-pressed={isActive}
                                    >
                                        <div className="min-h-0">
                                            <div className="mb-3 flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-2">
                                                    <div className={`inline-flex h-9 w-9 items-center justify-center rounded-md shadow-sm ring-1 ${isActive
                                                        ? 'bg-stone-900 text-white ring-stone-900'
                                                        : 'bg-white text-stone-700 ring-stone-200'}`}>
                                                        <Icon className="h-[18px] w-[18px]" />
                                                    </div>
                                                    <span className={`whitespace-nowrap rounded-full border px-2 py-1 text-[11px] font-medium transition-opacity ${isActive
                                                        ? 'border-emerald-100 bg-emerald-50 text-emerald-700 opacity-100'
                                                        : 'border-transparent bg-transparent text-transparent opacity-0'}`}>
                                                        {t('pageHome.content.newFeatures.selectedBadge')}
                                                    </span>
                                                </div>
                                                <span className="rounded-full border border-stone-200 bg-white px-2.5 py-1 text-xs font-medium text-stone-600">
                                                    {badge}
                                                </span>
                                            </div>
                                            <h3 className="text-base font-semibold text-stone-950">{title}</h3>
                                            <p className="mt-2 text-sm leading-6 text-stone-600">{text}</p>
                                            <p className="mt-3 line-clamp-1 text-xs font-medium text-stone-500">{meta}</p>
                                        </div>
                                        <span className="mt-4 inline-flex shrink-0 items-center text-sm font-medium text-stone-900">
                                            {isActive
                                                ? t('pageHome.content.newFeatures.selected')
                                                : t('pageHome.content.newFeatures.preview')}
                                            <ArrowRight className="ml-1 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                                        </span>
                                    </button>
                                );
                            })}
                        </div>

                        <div className="lg:col-span-7 lg:min-h-[560px]">
                            {activeFeature === 'ai' ? (
                                <div className="flex flex-col lg:min-h-[560px]">
                                    <div className="relative overflow-hidden rounded-lg border border-stone-200 bg-stone-950 shadow-sm">
                                        <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-24 bg-gradient-to-b from-black/30 to-transparent" />
                                        <video
                                            ref={videoRef}
                                            className="block aspect-video w-full object-cover"
                                            src={videoSrc}
                                            poster={poster}
                                            autoPlay
                                            muted
                                            loop
                                            playsInline
                                            preload="metadata"
                                        />
                                        <div className="absolute left-4 top-4 z-10 inline-flex items-center gap-2 rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white backdrop-blur">
                                            <Bot className="h-3.5 w-3.5" />
                                            {t('pageHome.content.newFeatures.videoLabel')}
                                        </div>
                                        <button
                                            type="button"
                                            onClick={togglePlay}
                                            className="absolute bottom-4 left-4 z-10 rounded-full bg-black/70 px-3 py-1.5 text-xs font-medium text-white backdrop-blur transition-colors hover:bg-black/80"
                                        >
                                            {isPlaying
                                                ? t('pageHome.content.newFeatures.pause')
                                                : t('pageHome.content.newFeatures.play')}
                                        </button>
                                    </div>

                                    <div className="mt-3 rounded-lg border border-stone-200 bg-stone-50/70 p-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <div className="flex items-center gap-2 text-sm font-semibold text-stone-950">
                                                <LayoutDashboard className="h-4 w-4 text-stone-700" />
                                                {t('pageHome.content.newFeatures.aiPreviewTitle')}
                                            </div>
                                            <a
                                                href={activeFeatureData.href}
                                                className="inline-flex shrink-0 items-center gap-1 text-xs font-semibold text-stone-900"
                                            >
                                                {t('pageHome.content.newFeatures.open')}
                                                <ArrowRight className="h-3.5 w-3.5" />
                                            </a>
                                        </div>
                                        <div className="mt-3 grid gap-2 sm:grid-cols-3">
                                            {aiPreviewItems.map((item) => (
                                                <div
                                                    key={item}
                                                    className="rounded-md border border-stone-200 bg-white px-3 py-2 text-xs font-medium text-stone-700 shadow-sm"
                                                >
                                                    {item}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="rounded-lg border border-stone-200 bg-stone-50/70 p-4 shadow-sm lg:min-h-[560px]">
                                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                                        <div>
                                            <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700">
                                                <MousePointer2 className="h-3.5 w-3.5" />
                                                {t('pageHome.content.newFeatures.statisticsDemoBadge')}
                                            </div>
                                            <h3 className="mt-3 text-xl font-semibold text-stone-950">
                                                {t('pageHome.content.newFeatures.statisticsDemoTitle')}
                                            </h3>
                                            <p className="mt-2 text-sm leading-6 text-stone-600">
                                                {t('pageHome.content.newFeatures.statisticsDemoText')}
                                            </p>
                                        </div>
                                        <a
                                            href={activeFeatureData.href}
                                            className="inline-flex h-9 w-fit shrink-0 items-center justify-center gap-2 rounded-full bg-stone-900 px-4 text-sm font-medium text-white shadow transition-colors hover:bg-stone-900/90"
                                        >
                                            {t('pageHome.content.newFeatures.open')}
                                            <ArrowRight className="h-4 w-4" />
                                        </a>
                                    </div>

                                    <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
                                        <div>
                                            <div className="mb-2 text-xs font-semibold uppercase text-stone-500">
                                                {t('pageHome.content.newFeatures.metricLabel')}
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {metricOptions.map((metric) => (
                                                    <button
                                                        key={metric.id}
                                                        type="button"
                                                        onClick={() => setActiveMetric(metric.id)}
                                                        className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${activeMetric === metric.id
                                                            ? 'border-stone-900 bg-stone-900 text-white'
                                                            : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300'}`}
                                                    >
                                                        {metric.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="mb-2 text-xs font-semibold uppercase text-stone-500">
                                                {t('pageHome.content.newFeatures.regionLabel')}
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {regionOptions.map((region) => (
                                                    <button
                                                        key={region.id}
                                                        type="button"
                                                        onClick={() => setActiveRegion(region.id)}
                                                        className={`cursor-pointer rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${activeRegion === region.id
                                                            ? 'border-blue-600 bg-blue-600 text-white'
                                                            : 'border-stone-200 bg-white text-stone-700 hover:border-stone-300'}`}
                                                    >
                                                        {region.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="mt-4 rounded-lg border border-stone-200 bg-white p-4">
                                        <div className="flex items-start justify-between gap-3">
                                            <div>
                                                <div className="text-xs font-medium text-stone-500">{selectedMetric.label}</div>
                                                <div className="mt-1 text-2xl font-semibold text-stone-950">{selectedMetric.value}</div>
                                            </div>
                                            <div className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700">
                                                <TrendingUp className="h-3.5 w-3.5" />
                                                {selectedMetric.change}
                                            </div>
                                        </div>
                                        <div className="mt-4 flex h-32 items-end gap-2">
                                            {chartBars.map((height, index) => (
                                                <div key={chartLabels[index]} className="flex min-w-0 flex-1 flex-col items-center gap-2">
                                                    <div className="flex h-24 w-full items-end rounded-md bg-stone-100 px-1.5 pb-1.5">
                                                        <div
                                                            className="w-full rounded-sm bg-blue-600 transition-all duration-300"
                                                            style={{ height: `${height}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-[11px] font-medium text-stone-500">{chartLabels[index]}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="mt-3 rounded-lg border border-stone-200 bg-white p-4">
                                        <div className="flex items-center gap-2 text-sm font-semibold text-stone-950">
                                            <CircleDollarSign className="h-4 w-4 text-stone-700" />
                                            {t('pageHome.content.newFeatures.statisticsPreviewTitle')}
                                        </div>
                                        <div className="mt-3 grid gap-2 sm:grid-cols-3">
                                            {statisticsPreviewItems.map((item) => (
                                                <div
                                                    key={item}
                                                    className="rounded-md border border-stone-200 bg-stone-50 px-3 py-2 text-xs font-medium text-stone-700"
                                                >
                                                    {item}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default VideoHero;
